import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Stack, 
  Button, 
  Avatar, 
  IconButton,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Paper
} from "@mui/material";
import { 
  PlayArrowRounded,
  Share,
  MoreVert,
  Send,
  ThumbUp,
  ThumbUpOutlined,
  Delete,
  Lock,
  LockOpen
} from "@mui/icons-material";
import VideoPlayer from "../../components/VideoPlayer";
import VideoPlayerEnhanced from "../../components/VideoPlayer/VideoPlayerEnhanced";
import VideoBoxEnhanced from "../../components/VideoBox/VideoBoxEnhanced";
import FavoriteButton from "../../common/FavoriteButton";
import ApiService from '../../services/api-service';
import VideoApiService from '../../services/video-api-service';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { fullName, getUserId } from "./../../constants/function";

const MusicDetailsCardVideo = ({
  playListId,
  selectedVideo,
  onVideoSelect,
  onVideoUpdate,
  videos = [],
  recordings = [],
  isOwner,
  createdByDetails
}) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [videoDetails, setVideoDetails] = useState(null);
  const [isVerticalVideo, setIsVerticalVideo] = useState(false);
  const [userVideoSaves, setUserVideoSaves] = useState([]);
  const [isPrivate, setIsPrivate] = useState(selectedVideo?.private || false);
  const viewCountedRef = React.useRef(false);
  
  console.log('MusicDetailsCardVideo - isOwner:', isOwner);
  console.log('MusicDetailsCardVideo - selectedVideo:', selectedVideo);
  console.log('MusicDetailsCardVideo - createdByDetails:', createdByDetails);
  console.log('MusicDetailsCardVideo - createdBy ID:', createdByDetails?.createdBy);
  console.log('MusicDetailsCardVideo - Current user ID:', getUserId());

  // Combine videos and recordings for the sidebar
  const allVideos = [...recordings, ...videos];

  useEffect(() => {
    // Load user's video saves
    const loadVideoSaves = async () => {
      try {
        console.log('Loading video saves...');
        const response = await ApiService.GET_SAVES('videos');
        console.log('Video saves response:', response);
        console.log('Response keys:', Object.keys(response || {}));
        
        // Try different possible response formats
        let videoSaves = [];
        if (response?.data) {
          videoSaves = response.data;
        } else if (response?.result) {
          videoSaves = response.result;
        } else if (Array.isArray(response)) {
          videoSaves = response;
        } else if (response?.videos) {
          videoSaves = response.videos;
        }
        
        console.log('Extracted video saves:', videoSaves);
        setUserVideoSaves(Array.isArray(videoSaves) ? videoSaves : []);
      } catch (error) {
        console.error('Error loading video saves:', error);
        console.error('Error details:', error.response?.data);
        // Set empty array on error so the component still works
        setUserVideoSaves([]);
      }
    };
    
    loadVideoSaves();
  }, []);

  useEffect(() => {
    if (selectedVideo) {
      setIsPrivate(selectedVideo.private || false);
    }
  }, [selectedVideo]);

  useEffect(() => {
    if (selectedVideo) {
      fetchVideoDetails();
      fetchComments();
      // Reset view tracking when video changes
      viewCountedRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideo?._id]); // Only re-run when video ID changes

  // Track view when selectedVideo is loaded
  useEffect(() => {
    if (selectedVideo && !viewCountedRef.current) {
      console.log('[MusicDetailsCardVideo] Starting 3-second view tracking timer...');
      // Track view after 3 seconds
      const timer = setTimeout(() => {
        console.log('[MusicDetailsCardVideo] 3 seconds elapsed, calling trackView()');
        trackView();
      }, 3000);
      
      return () => {
        console.log('[MusicDetailsCardVideo] Cleanup: clearing view tracking timer');
        clearTimeout(timer);
      };
    }
  }, [selectedVideo]);

  const trackView = async () => {
    console.log('[trackView] Called - viewCountedRef:', viewCountedRef.current, 'selectedVideo:', !!selectedVideo);
    
    if (viewCountedRef.current || !selectedVideo) {
      console.log('[trackView] Skipping - already counted or no video data');
      return;
    }
    
    try {
      // Get studioId - it could be from selectedVideo or playListId
      const studioId = selectedVideo.studioId || selectedVideo.uploader?._id || selectedVideo.creator?._id || playListId;
      console.log('[trackView] Found studioId:', studioId, 'from video:', {
        studioId: selectedVideo.studioId,
        uploaderId: selectedVideo.uploader?._id,
        creatorId: selectedVideo.creator?._id,
        playListId: playListId
      });
      
      if (!studioId) {
        console.error('[trackView] Cannot track view: No studioId found');
        return;
      }
      
      console.log('[trackView] Calling API - videoId:', selectedVideo._id, 'studioId:', studioId);
      const response = await VideoApiService.trackView(selectedVideo._id, studioId);
      console.log('[trackView] API Response:', response);
      
      if (response.success || response.ok) {
        viewCountedRef.current = true;
        console.log('[trackView] View tracked successfully!');
        // Update video details if needed
        if (onVideoUpdate && selectedVideo) {
          const updatedVideo = { ...selectedVideo, views: (selectedVideo.views || 0) + 1 };
          onVideoUpdate(updatedVideo);
        }
      } else {
        console.error('[trackView] API returned unsuccessful response:', response);
      }
    } catch (error) {
      console.error('[trackView] Error tracking view:', error.response || error);
    }
  };

  const fetchVideoDetails = async () => {
    // Fetch video details like views, likes, etc.
    try {
      // This would be your API call to get video details
      console.log('Fetching details for video:', selectedVideo);
    } catch (error) {
      console.error('Error fetching video details:', error);
    }
  };

  const fetchComments = async () => {
    if (!selectedVideo?._id) return;
    
    setLoadingComments(true);
    try {
      const response = await VideoApiService.getComments(selectedVideo._id, 1, 20);
      console.log('MusicDetailsCardVideo - Comments response:', response);
      if (response?.ok) {
        // Transform API response to match our component structure
        const { comments } = response.result;
        console.log('MusicDetailsCardVideo - Raw comments:', comments);
        const transformedComments = comments.map(comment => ({
          id: comment._id,
          user: {
            name: comment.user?.firstName || comment.user?.lastName ? `${comment.user.firstName || ''} ${comment.user.lastName || ''}`.trim() : 'Anonymous',
            avatar: comment.user?.profileImg?.filePath || null,
            _id: comment.user?._id
          },
          text: comment.content,
          timestamp: new Date(comment.createdAt),
          likes: comment.likesCount || 0,
          isLiked: comment.isLiked || false,
          isOwn: (() => {
            const userId = getUserId();
            const isOwner = comment.user?._id === userId;
            console.log('Comment ownership check:', {
              commentId: comment._id,
              commentUserId: comment.user?._id,
              currentUserId: userId,
              isOwn: isOwner
            });
            return isOwner;
          })()
        }));
        setComments(transformedComments);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleCommentSubmit = async () => {
    if (!newComment.trim() || !selectedVideo?._id) return;
    
    try {
      const response = await VideoApiService.postComment(selectedVideo._id, newComment.trim());
      console.log('Comment response:', response);
      if (response?.ok) {
        // Add the new comment to the top of the list
        const newCommentObj = {
          id: response.result._id,
          user: {
            name: response.result.user?.firstName || response.result.user?.lastName ? `${response.result.user.firstName || ''} ${response.result.user.lastName || ''}`.trim() : 'You',
            avatar: response.result.user?.profileImg?.filePath || null,
            _id: response.result.user?._id
          },
          text: response.result.content,
          timestamp: new Date(response.result.createdAt),
          likes: 0,
          isLiked: false,
          isOwn: true
        };
        setComments([newCommentObj, ...comments]);
        setNewComment('');
      } else {
        console.error('Failed to post comment - response:', response);
      }
    } catch (error) {
      console.error('Error posting comment:', error);
    }
  };

  const handleCommentLike = async (commentId, isLiked) => {
    try {
      const response = isLiked 
        ? await VideoApiService.unlikeComment(commentId)
        : await VideoApiService.likeComment(commentId);
      
      if (response?.ok) {
        // Update the comment in the local state
        setComments(prevComments => 
          prevComments.map(comment => 
            comment.id === commentId 
              ? {
                  ...comment,
                  isLiked: !isLiked,
                  likes: isLiked ? comment.likes - 1 : comment.likes + 1
                }
              : comment
          )
        );
      }
    } catch (error) {
      console.error('Error toggling comment like:', error);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      const response = await VideoApiService.deleteComment(commentId);
      if (response?.ok) {
        setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
        console.log('Comment deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const handleVideoSaveToggle = (videoId, isSaved) => {
    if (isSaved) {
      setUserVideoSaves(prev => [...prev, videoId]);
    } else {
      setUserVideoSaves(prev => prev.filter(id => id !== videoId));
    }
  };

  const handlePrivacyToggle = async (video) => {
    if (!video || !isOwner) return;
    
    const targetVideo = video || selectedVideo;
    const newPrivateStatus = !targetVideo.private;
    
    try {
      const payload = {
        assetId: targetVideo._id,
        type: 'video',
        private: newPrivateStatus
      };
      console.log('Sending privacy update payload:', payload);
      console.log('Target video:', targetVideo);
      
      const response = await ApiService.ASSET_PRIVACY(payload);
      
      if (response?.ok || response?.success) {
        // Update the parent component's state
        if (onVideoUpdate) {
          onVideoUpdate({ _id: targetVideo._id, private: newPrivateStatus });
        }
        
        // Update local state if it's the selected video
        if (selectedVideo?._id === targetVideo._id) {
          setIsPrivate(newPrivateStatus);
        }
        
        console.log(`Video marked as ${newPrivateStatus ? 'private' : 'public'}`);
      }
    } catch (error) {
      console.error('Error updating video privacy:', error);
    }
  };

  // Adjust layout based on video orientation
  const getMainLayoutStyle = () => {
    return {
      display: 'flex',
      gap: 1.5,
      height: '100vh',
      p: 1,
      backgroundColor: '#0a0a0a',
      overflow: 'hidden',
      // Keep same layout for both vertical and horizontal videos
      // Video player and comments always on left, video list always on right
    };
  };

  const getVideoContainerStyle = () => {
    // Use consistent width for both vertical and horizontal videos
    return {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      overflow: 'hidden',
    };
  };

  return (
    <Box sx={getMainLayoutStyle()}>
      {/* Main Content Area - Video Player and Comments */}
      <Box sx={getVideoContainerStyle()}>
        {/* Video Player Container */}
        <Paper 
          elevation={0}
          sx={{ 
            backgroundColor: 'var(--background-color, #000)',
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
            height: '50vh', // Increased height for better video viewing
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {selectedVideo ? (
            <VideoPlayerEnhanced
              video={selectedVideo}
              studioId={playListId}
              onAspectRatioDetected={(aspectRatio) => {
                setIsVerticalVideo(aspectRatio < 1);
              }}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          ) : (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              backgroundColor: '#1a1a1a',
            }}>
              <Typography color="text.secondary">
                Select a video from the list to start watching
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Video Info */}
        {selectedVideo && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ color: '#fff', mb: 0.5, fontWeight: 600 }}>
              {selectedVideo.title || 'Untitled Video'}
            </Typography>
            
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {selectedVideo.views || 0} views
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                {selectedVideo.createdAt ? formatDistanceToNow(new Date(selectedVideo.createdAt), { addSuffix: true }) : ''}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <FavoriteButton
                itemId={selectedVideo._id}
                itemType="videos"
                userSaves={userVideoSaves}
                onSaveToggle={handleVideoSaveToggle}
                size={20}
                sx={{ 
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.4)',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  }
                }}
              />
              <Button
                size="small"
                startIcon={<Share sx={{ fontSize: 16 }} />}
                onClick={async () => {
                  if (selectedVideo) {
                    const shareUrl = `${window.location.origin}/app/video-player/${playListId}/${selectedVideo._id}`;
                    
                    // Check if Web Share API is available (mobile Safari supports it)
                    if (navigator.share && /iPad|iPhone|iPod/.test(navigator.userAgent)) {
                      try {
                        await navigator.share({
                          title: selectedVideo.title || 'Check out this video',
                          text: `Watch "${selectedVideo.title || 'this video'}" on Siki`,
                          url: shareUrl
                        });
                      } catch (err) {
                        if (err.name !== 'AbortError') {
                          console.log('Error sharing:', err);
                        }
                      }
                    } else {
                      // Use clipboard API with better Safari support
                      const copyToClipboard = async (text) => {
                        try {
                          // Modern clipboard API
                          if (navigator.clipboard && window.isSecureContext) {
                            await navigator.clipboard.writeText(text);
                            return true;
                          }
                          
                          // Fallback for Safari and older browsers
                          const textArea = document.createElement('textarea');
                          textArea.value = text;
                          textArea.style.position = 'fixed';
                          textArea.style.left = '-999999px';
                          textArea.style.top = '-999999px';
                          document.body.appendChild(textArea);
                          textArea.focus();
                          textArea.select();
                          
                          // For iOS Safari
                          if (navigator.userAgent.match(/ipad|iphone/i)) {
                            const range = document.createRange();
                            range.selectNodeContents(textArea);
                            const selection = window.getSelection();
                            selection.removeAllRanges();
                            selection.addRange(range);
                            textArea.setSelectionRange(0, 999999);
                          }
                          
                          const result = document.execCommand('copy');
                          document.body.removeChild(textArea);
                          return result;
                        } catch (err) {
                          console.error('Failed to copy:', err);
                          return false;
                        }
                      };
                      
                      const success = await copyToClipboard(shareUrl);
                      if (success) {
                        // Show a toast or better UI feedback instead of alert
                        alert('Video link copied to clipboard!');
                      } else {
                        alert('Failed to copy link. Please copy manually: ' + shareUrl);
                      }
                    }
                  }
                }}
                sx={{ 
                  color: '#fff', 
                  borderColor: 'rgba(255,255,255,0.2)',
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  '&:hover': {
                    borderColor: 'rgba(255,255,255,0.4)',
                    backgroundColor: 'rgba(255,255,255,0.05)'
                  }
                }}
                variant="outlined"
              >
                Share
              </Button>
            </Stack>
          </Box>
        )}

        {/* Comments Section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: '#1a1a1a',
          borderRadius: 1,
          p: 1.5,
          mt: 1,
          flex: 1,
          overflow: 'hidden',
        }}>
          <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1, fontWeight: 'bold' }}>
            Comments ({comments.length})
          </Typography>

          {/* Comment Input */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.875rem' }}>U</Avatar>
            <TextField
              fullWidth
              size="small"
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleCommentSubmit()}
              sx={{
                '& .MuiInputBase-root': {
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255,255,255,0.2)',
                },
              }}
            />
            <IconButton 
              onClick={handleCommentSubmit}
              disabled={!newComment.trim()}
              sx={{ color: '#fff' }}
            >
              <Send />
            </IconButton>
          </Box>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 1 }} />

          {/* Comments List */}
          <List sx={{ 
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255,255,255,0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '3px',
            },
          }}>
            {loadingComments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>Loading comments...</Typography>
              </Box>
            ) : comments.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>No comments yet. Be the first to comment!</Typography>
              </Box>
            ) : (
              comments.map((comment) => (
                <ListItem key={comment.id} alignItems="flex-start" sx={{ px: 0, py: 1 }}>
                  <ListItemAvatar sx={{ minWidth: 40 }}>
                    <Avatar src={comment.user.avatar} sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                      {comment.user.name[0]?.toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                        {comment.user.name}
                      </Typography>
                      {comment.isOwn && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
                          (You)
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {formatDistanceToNow(comment.timestamp, { addSuffix: true })}
                      </Typography>
                    </Stack>
                    
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5, wordBreak: 'break-word', display: 'block' }}>
                      {comment.text}
                    </Typography>
                    
                    {/* Comment Actions */}
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton
                          size="small"
                          onClick={() => handleCommentLike(comment.id, comment.isLiked)}
                          sx={{ 
                            color: comment.isLiked ? '#1976d2' : 'rgba(255,255,255,0.5)',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.05)'
                            }
                          }}
                        >
                          {comment.isLiked ? <ThumbUp fontSize="small" /> : <ThumbUpOutlined fontSize="small" />}
                        </IconButton>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          {comment.likes > 0 ? comment.likes : ''}
                        </Typography>
                      </Stack>
                      
                      {comment.isOwn && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteComment(comment.id)}
                          sx={{ 
                            color: 'rgba(255,255,255,0.5)',
                            '&:hover': {
                              backgroundColor: 'rgba(255,255,255,0.05)',
                              color: '#f44336'
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </Box>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </Box>

      {/* Right Sidebar - Video List */}
      <Box sx={{ 
        width: 320,
        flex: '0 0 320px',
        display: 'flex', 
        flexDirection: 'column',
        backgroundColor: '#1a1a1a',
        borderRadius: 1,
        p: 1.5,
        height: '100%',
        overflow: 'hidden'
      }}>
        <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1, fontWeight: 'bold' }}>
          Up Next
        </Typography>
        
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.3)',
            borderRadius: '4px',
          },
        }}>
          <Stack spacing={1}>
            {allVideos.map((video, index) => (
              <Box
                key={video._id || index}
                sx={{
                  borderRadius: 1,
                  p: 0.5,
                  backgroundColor: selectedVideo?.url === video.url ? 'rgba(255,255,255,0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                <Stack direction="row" spacing={2} onClick={() => onVideoSelect(video)} sx={{ cursor: 'pointer' }}>
                  {/* Thumbnail */}
                  <Box
                    sx={{
                      width: 100,
                      height: 75,
                      borderRadius: 1,
                      overflow: 'hidden',
                      backgroundColor: 'var(--background-color, #000)',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={video.thumbnailUrl || video.thumbnailImg || '/placeholder-video.jpg'}
                      alt={video.title}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        width: 'auto',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                  
                  {/* Video Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#fff',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        mb: 0.5,
                      }}
                    >
                      {video.title || 'Untitled'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                      {video.views || 0} views
                    </Typography>
                    {isOwner && (
                      <Box 
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrivacyToggle(video);
                        }}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mt: 0.5,
                          cursor: 'pointer',
                          '&:hover': {
                            '& svg, & .MuiTypography-root': {
                              color: 'rgba(255,255,255,0.8)'
                            }
                          }
                        }}
                      >
                        {video.private ? (
                          <Lock sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', mr: 0.5 }} />
                        ) : (
                          <LockOpen sx={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', mr: 0.5 }} />
                        )}
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          {video.private ? 'Private' : 'Public'}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default MusicDetailsCardVideo;